<template>
  <div>
    <PageHeader :title="title" :items="items"/>
    <b-col class="text-end" sm="12">
      <remaining :used="used" :total="total"></remaining>
    </b-col>
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <div class="col-sm-12">
              <b-row class="justify-content-between">
                <b-col sm="3" class="pt-1">
                  <b-form-select class="form-select mb-3"
                                 v-model="templateType"
                                 :options="templateTypeOptions"
                                 @change="handleTemplateType"/>
                </b-col>
                <b-col sm="3" class="pt-1">
                  <date-picker v-model="dateRange"
                               range
                               append-to-body
                               :lang="$i18n.locale"
                               confirm
                               @confirm="handleDatePicker"
                               :placeholder="$t('pages.templates.date_range')"/>
                </b-col>
                <b-col sm="3" offset-xl="3" offset-lg="3" class="pt-1 text-end">
                  <b-btn
                      variant="success"
                      class="mb-2 me-2"
                      @click="openNewTemplate"
                      :disabled="disabledQuota"
                  >
                    <i class="mdi mdi-tooltip-plus"></i>
                    {{ $t('pages.templates.new_template') }}
                  </b-btn>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
        <div class="col-lg-12">
          <div class="card">
            <div class="card-body">
              <div class="col-sm-12">
                <div class="row">
                  <div class="col-sm-6 col-12">
                    <PerPageSelector v-model="query.perPage"
                                     :available-per-page-options="availablePerPageOptions"
                    ></PerPageSelector>
                  </div>
                  <div class="col-sm-3 offset-lg-3 offset-md-3 offset-sm-3 col-12">
                    <DataTableSearch v-model="query.filter"
                                     :place-holder="$t('pages.templates.search')">
                    </DataTableSearch>
                  </div>
                </div>
              </div>
              <div>
                <b-table
                    ref="templateTable"
                    striped
                    hover
                    show-empty
                    :empty-text="$t('components.bootstrap_table.empty_text')"
                    :empty-filtered-text="$t('components.bootstrap_table.empty_filtered_text')"
                    :fields="fields"
                    :items="getTemplates"
                    :stacked="$store.state.app.isMobile"
                    :filter="query.filter"
                    :per-page="query.perPage"
                    :sort-by="query.sortBy"
                    :sort-desc="query.sortDesc"
                    :current-page="query.page"
                >
                  <template v-slot:cell(type)="data">
                    <h5>
                      <span class="badge badge-soft-primary">
                        <i class="mdi mdi-email-fast-outline"></i>
                        {{ templateTypeOptions.find(x => x.value === data.item.type).text }}
                      </span>
                    </h5>
                  </template>

                  <template v-slot:cell(action)="data">
                    <b-dropdown
                        class="card-drop"
                        variant="white"
                        right
                        toggle-class="p-0"
                        menu-class="dropdown-menu-end"
                    >
                      <template v-slot:button-content>
                        <i class="mdi mdi-dots-horizontal font-size-18"></i>
                      </template>

                      <b-dropdown-item @click="showTemplate(data.item.id)">
                        <i class="fas fa-pencil-alt text-success me-1"></i>
                        {{ $t('pages.templates.edit') }}
                      </b-dropdown-item>

                      <b-dropdown-item @click="deleteTemplate(data.item)">
                        <i class="fas fa-trash-alt text-danger me-1"></i>
                        {{ $t('pages.templates.delete') }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </template>
                </b-table>
                <div class="row">
                  <div class="col-sm">
                    <Pagination v-model="query.page" :meta="templates.meta"></Pagination>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from "../../components/Pagination";
import queryBuilder from "../../mixins/queryBuilder";
import EmailService from "../../Services/Api/EmailService";
import DataTableSearch from "../../components/DataTableSearch";
import PerPageSelector from "../../components/PerPageSelector";
import DatePicker from "vue2-datepicker";
import Remaining from "../../components/Remaining";
import QuotaMixin from "../../mixins/QuotaMixin";
import Swal from "sweetalert2";

export default {
  name: "TemplateList",
  mixins: [queryBuilder, QuotaMixin],
  components: {
    Remaining,
    PerPageSelector,
    DataTableSearch,
    Pagination,
    DatePicker
  },
  page() {
    return {
      title: this.$t('pages.templates.title'),
      meta: [
        {
          name: "description",
          content: this.$t('common.app_title')
        }
      ]
    }
  },
  data() {
    return {
      items: [
        {
          text: this.$t('pages.templates.items.first'),
          href: "/",
        },
        {
          text: this.$t('pages.templates.items.second'),
          active: true,
        },
      ],
      fields: [
        {key: 'name', sortable: false, label: this.$t('pages.templates.fields.name')},
        {key: 'type', sortable: false, label: this.$t('pages.templates.fields.type')},
        {key: 'language_variants', sortable: false, label: this.$t('pages.templates.fields.variants')},
        {key: 'created_at', sortable: false, label: this.$t('pages.templates.fields.created_at')},
        {key: 'action', sortable: false, label: this.$t('pages.templates.fields.action')},
      ],
      templateTypeOptions: [
        {
          text: this.$t('pages.templates.template_options.title'),
          value: null
        },
        {
          text: this.$t('pages.templates.template_options.confirmCustomizationDetails'),
          value: 'confirmCustomizationDetails'
        },
        {
          text: this.$t('pages.templates.template_options.confirmDeliveryDetails'),
          value: 'confirmDeliveryDetails'
        },
        {
          text: this.$t('pages.templates.template_options.confirmOrderDetails'),
          value: 'confirmOrderDetails'
        },
        {
          text: this.$t('pages.templates.template_options.confirmServiceDetails'),
          value: 'confirmServiceDetails'
        },
        {
          text: this.$t('pages.templates.template_options.warranty'),
          value: 'warranty'
        },
        {
          text: this.$t('pages.templates.template_options.customEmail'),
          value: 'customEmail'
        }
      ],
      title: "Templates",
      dateRange: null,
      templateType: null,
      templates: [],
      quotaType: 'custom_template'
    }
  },
  methods: {
    async getTemplates(params = this.query) {
      this.query = {...this.query, ...params}
      const query = this.buildQuery()
      const {data: templates} = await EmailService.all({query})
      this.templates = templates
      return templates.data
    },
    handleTemplateType() {
      this.query.templateType = this.templateType
      this.$refs.templateTable.refresh()
    },
    handleDatePicker() {
      this.query.dateRange = this.dateRange
      this.$refs.templateTable.refresh()
    },
    showTemplate(templateId) {
      this.$router.push({name: 'email.design', params: {emailID: templateId}})
    },
    openNewTemplate() {
      this.$router.push({name: 'new.template'})
    },
    async deleteTemplate(template) {
      Swal.fire({
        title: this.$t('pages.templates.delete_title', {name: template.name}),
        icon: "error",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonColor: "#f46a6a",
        cancelButtonColor: "#34c38f",
        confirmButtonText: this.$t('pages.templates.delete')
      }).then(result => {
        if (result.value) {
          EmailService.destroy({id: template.id}).then(() => {
            this.$store.dispatch('quota/getSubscriptionQuota')
            this.$refs.templateTable.refresh()
          }).catch((e) => {
            if (e.status === 409) {
              Swal.fire(this.$t('pages.templates.delete_error_title'), this.$t('pages.templates.delete_error_text'), "error");
            }
          })
        }
      });

    }
  },
}
</script>

<style scoped>

</style>